import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { CartContext } from '../../context/CartContext';
import LoginModal from '../shared/LoginModal';
import { menuAPI, resolveScopedRestaurantId } from '../../utils/api';

// grouped categories with subcategories
const CATEGORY_GROUPS = [
  {
    id: 'main',
    title: 'Main Dishes',
    subs: [
      'Appetizer',
      'Breakfast Meals',
      'Chicken',
      'Pork',
      'Beef',
      'Sariwang Gulay',
      'Sinigang',
      'Lamang Dagat',
      'Bandehadong Almusal'
    ]
  },
  {
    id: 'pasta',
    title: 'Pasta & Pancit',
    subs: ['Casa Pasta', 'Espesyal Pancit & Lomi', 'Pancit | Pasta Tray']
  },
  {
    id: 'desserts',
    title: 'Desserts & Cakes',
    subs: ['Minatamis']
  },
  {
    id: 'drinks',
    title: 'Drinks',
    subs: ['Veggie Salads', 'Pitcher Juice', 'Coffee', 'Coffee & Ice Cream', 'Coffee-Free', 'Milkshake', 'Cocktails', 'Bottled Beers', 'Soda & Water', 'Fresh Fruit Shakes', 'Healthy Options', 'Mocktails', 'Wine']
  },
  {
    id: 'specials',
    title: 'Specials',
    subs: ['Kahanggang Salo-Salo', 'Kanin sa Pinggan', 'Party Trays']
  },
  {
    id: 'burgers',
    title: 'Burgers & Sandwiches',
    subs: ['Burger']
  }
];

// All menu items now loaded from database via API

const MenuSection = () => {
  const { user } = useContext(AuthContext);
  const [activeCat, setActiveCat] = useState('All');
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dynamicGroups, setDynamicGroups] = useState(CATEGORY_GROUPS);

  // search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);

  // Function to build dynamic category groups based on actual menu items
  const buildDynamicGroups = (items) => {
    if (!items || items.length === 0) {
      return CATEGORY_GROUPS;
    }

    // Get all unique categories from menu items
    const availableCategories = new Set(items.map(item => item.category).filter(Boolean));
    // Filter CATEGORY_GROUPS to only include groups with available subcategories
    const filtered = CATEGORY_GROUPS.map(group => {
      // Only include subcategories that exist in the menu
      const validSubs = group.subs.filter(sub => availableCategories.has(sub));
      
      // Only return group if it has at least one valid subcategory
      if (validSubs.length > 0) {
        return {
          ...group,
          subs: validSubs
        };
      }
      return null;
    }).filter(Boolean);
    
    return filtered;
  };

  // Fetch menu items from API
  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        setLoading(true);
        
        // Build API parameters
        const params = { per_page: 10000 };
        
        // Resolve restaurant scope from user context + URL/localStorage
        const scopedRestaurantId = resolveScopedRestaurantId(user);

        // Staff stay scoped to their restaurant; everyone else follows the resolved scope
        if (user?.role === 'staff' && user?.restaurant_id) {
          params.restaurant_id = user.restaurant_id;
          localStorage.setItem('selected_restaurant_id', user.restaurant_id.toString());
        } else if (scopedRestaurantId) {
          params.restaurant_id = scopedRestaurantId;
          localStorage.setItem('selected_restaurant_id', scopedRestaurantId.toString());
        }
        
        const response = await menuAPI.getAll(params);
        
        // Handle paginated response: response.data.data.data or response.data.data
        let apiItems = [];
        if (response.data && response.data.data) {
          if (response.data.data.data) {
            // Paginated response: { success: true, data: { data: [...], per_page, etc } }
            apiItems = response.data.data.data;
          } else if (Array.isArray(response.data.data)) {
            // Array response: { success: true, data: [...] }
            apiItems = response.data.data;
          }
        } else if (Array.isArray(response.data)) {
          // Direct array: response.data = [...]
          apiItems = response.data;
        }
        
          // Convert API items to menu item format
          const apiMenuItems = apiItems.map((item, idx) => {
            let category = item.category || 'Drinks';
            
            // Map generic "Drinks" category to specific subcategories based on item name
            if (category.toLowerCase() === 'drinks') {
              const name = (item.name || '').toLowerCase();
              if (name.includes('coke') || name.includes('sprite') || name.includes('royal') || name.includes('water')) {
                category = 'Soda & Water';
              } else if (name.includes('coffee') || name.includes('americano') || name.includes('latte') || name.includes('cappuccino')) {
                category = 'Coffee';
              } else if (name.includes('beer')) {
                category = 'Bottled Beers';
              } else if (name.includes('juice')) {
                category = 'Pitcher Juice';
              }
            }
            
            return {
              id: item.menu_item_id || item.id || (1000 + idx),
              menu_item_id: item.menu_item_id || item.id || (1000 + idx), // Store database ID for API calls
              title: item.name || item.title || '',
              price: item.price ? `PHP ${parseFloat(item.price).toFixed(2)}` : item.price,
              category: category,
              img: item.image_url || null,
              status: item.status || 'available',
              description: item.description || ''
            };
          });
          
          // Set menu items from API (no hardcoded fallback)
          setMenuItems(apiMenuItems);
          
          // Build dynamic category groups based on actual categories in menu items
          const filtered = buildDynamicGroups(apiMenuItems);
          setDynamicGroups(filtered);
      } catch (error) {
        // On error: show empty menu
        setMenuItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMenuItems();
  }, [user?.role, user?.restaurant_id]); // Only refetch when role or restaurant changes

  const visibleItems = menuItems.filter((it) => {
    let matchesCategory = false;
    
    if (activeCat === 'All') {
      matchesCategory = true;
    } else {
      // Check if activeCat matches item category directly
      matchesCategory = it.category === activeCat;
      
      // If no direct match, check if activeCat is a parent category
      if (!matchesCategory) {
        const parentGroup = dynamicGroups.find(g => g.title === activeCat);
        if (parentGroup) {
          // If activeCat is a parent, check if item category is in its subcategories
          matchesCategory = parentGroup.subs.includes(it.category);
        }
      }
    }
    
    if (!searchQuery.trim()) return matchesCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch = (it.title && it.title.toLowerCase().includes(q)) || (it.category && it.category.toLowerCase().includes(q));
    return matchesCategory && matchesSearch;
  });

  // Deduplicate visible items by title+price to avoid rendering repeated cards when the data contains duplicates
  const uniqueVisible = [...new Map(
    visibleItems.map((it) => [((it.title || '').toLowerCase().trim() + '|' + (it.price || '')), it])
  ).values()];

  // modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [qty, setQty] = useState(1);
  const isModalOpen = Boolean(selectedItem);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectedItem(null);
    };
    if (isModalOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isModalOpen]);

  // focus search input when opened
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const navigate = useNavigate();
  const { isLoggedIn, openLoginModal } = useContext(AuthContext);
  const { addItem } = useContext(CartContext);

  const openModal = (item) => {
    // When user clicks an item we still open the item details modal (existing behavior)
    setSelectedItem(item);
    setQty(1);
  };

  const closeModal = () => setSelectedItem(null);

  const changeQty = (delta) => setQty((q) => Math.max(1, q + delta));

  const addToTray = () => {
    // Guest-friendly: allow adding to cart even when not logged-in. Persisted in localStorage by CartContext.
    if (!selectedItem) return;
    
    // Check if item is available
    if (selectedItem.status === 'unavailable' || selectedItem.status === 'sold_out') {
      alert('This item is currently unavailable.');
      return;
    }
    
    try {
      // Parse price string (e.g., "PHP 159.00") to numeric value
      const priceMatch = selectedItem.price?.match(/[\d,]+\.?\d*/);
      const numericPrice = priceMatch ? parseFloat(priceMatch[0].replace(/,/g, '')) : 0;
      
      // Create item with numeric price for cart
      const cartItem = {
        ...selectedItem,
        price: numericPrice
      };
      
      addItem(cartItem, qty);
    } catch (e) {
      // Silent fail - user will see cart update or not
    }
    // Close modal but stay on the menu page (do NOT navigate to /cart)
    setSelectedItem(null);
  };

  const toggleGroup = (groupId) => {
    const group = dynamicGroups.find(g => g.id === groupId);
    if (expandedGroup === groupId) {
      // If clicking the same group, collapse it
      setExpandedGroup(null);
      setActiveCat('All');
    } else {
      // If clicking a new group, expand it and filter by parent category
      setExpandedGroup(groupId);
      setActiveCat(group.title); // Set to parent category title (e.g., "Main Dishes")
    }
  };

  return (
  <section id="menu" className="bg-white text-black py-12 sm:py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
  <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bebas text-center mb-6 text-black">MENU</h2>

        {/* groups row */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 flex-1">
              <button
                onClick={() => { setActiveCat('All'); setExpandedGroup(null); }}
                className={`py-2 px-3 sm:px-4 rounded-full text-xs sm:text-sm whitespace-nowrap ${
                activeCat === 'All' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              All
            </button>

            {dynamicGroups.map((g) => (
              <button
                key={g.id}
                onClick={() => toggleGroup(g.id)}
                className={`py-2 px-3 sm:px-4 rounded-full text-xs sm:text-sm whitespace-nowrap ${
                    expandedGroup === g.id ? 'bg-black text-white' : 'bg-gray-100 text-gray-700'
                  }`}
              >
                {g.title}
              </button>
            ))}
          </div>

          <div className="flex-shrink-0 flex items-center gap-2 self-end sm:self-auto">
            {searchOpen ? (
              <div className="flex items-center bg-white rounded-full px-3 py-2 border border-gray-300 shadow-sm">
                <input
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); } }}
                  className="bg-transparent outline-none text-xs sm:text-sm text-gray-900 placeholder:text-gray-400 px-2 w-32 sm:w-40 md:w-64"
                  placeholder="Search menu"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-gray-600 hover:text-gray-800 px-2"
                    aria-label="clear search"
                  >
                    ✕
                  </button>
                )}
                <button
                  onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                  className="ml-1 w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 hover:bg-gray-100"
                  aria-label="close search"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                aria-label="search menu"
                onClick={() => setSearchOpen(true)}
                className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 hover:bg-gray-100 shadow-sm"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* subcategories row (shows when a group is expanded) */}
        {expandedGroup && (
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {dynamicGroups.find((g) => g.id === expandedGroup)?.subs.map((sub) => (
                <button
                  key={sub}
                  onClick={() => setActiveCat(sub)}
                  className={`py-2.5 sm:py-2 px-4 sm:px-3 md:px-4 rounded-full text-xs sm:text-sm whitespace-nowrap transition-colors ${
                      activeCat === sub ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  {sub}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-gray-500">Loading menu...</div>
          </div>
        ) : uniqueVisible.length === 0 ? (
          <div className="flex flex-col justify-center items-center py-20 text-center">
            <svg className="w-24 h-24 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Menu Items Found</h3>
            <p className="text-gray-500 max-w-md">
              {user && user.role === 'staff' 
                ? "Your restaurant's menu is empty. Please add menu items through the admin panel." 
                : activeCat !== 'All' 
                  ? `No items found in ${activeCat}. Try selecting a different category.`
                  : 'No menu items available at the moment.'}
            </p>
          </div>
        ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6 lg:gap-8">{uniqueVisible.map((item) => {
            const isUnavailable = item.status === 'unavailable' || item.status === 'sold_out';
            return (
            <div
              key={item.id}
              role="button"
              tabIndex={0}
              onClick={() => openModal(item)}
              onKeyPress={(e) => { if (e.key === 'Enter') openModal(item); }}
              className={`bg-white rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 flex flex-col items-center shadow-sm min-h-[18rem] sm:min-h-[20rem] border border-gray-200 cursor-pointer hover:shadow-lg focus:outline-none transition-shadow active:scale-[0.98] ${isUnavailable ? 'opacity-60' : ''}`}
            >
              <div className="w-28 h-28 sm:w-32 sm:h-32 mb-4 sm:mb-5 rounded-md overflow-hidden flex items-center justify-center bg-gray-50">
                {item.img ? (
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                ) : (
                  <svg className="w-16 h-16 text-gray-400" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="30" fill="transparent" stroke="white" strokeOpacity="0.12" />
                    <path d="M20 38c2-6 10-7 14-4 4-3 12-2 14 4" stroke="white" strokeOpacity="0.85" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M24 30c1-3 5-5 8-3 3-2 7-1 8 3" stroke="white" strokeOpacity="0.6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <h3 className="text-sm sm:text-base font-montserrat font-bold mb-2 text-gray-900 text-center line-clamp-2">{item.title}</h3>
              <div className="w-1/2 border-t border-gray-200 my-2"></div>
              <p className="mb-3 sm:mb-4 text-xs sm:text-sm text-gray-600">{item.price}</p>
              {isUnavailable ? (
                <span className="mt-auto bg-gray-300 text-gray-600 px-5 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm cursor-not-allowed">
                  {item.status === 'sold_out' ? 'Sold Out' : 'Unavailable'}
                </span>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); openModal(item); }}
                  className="mt-auto bg-black hover:bg-gray-800 text-white px-5 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm transition-colors active:scale-95"
                  aria-label={`Order ${item.title}`}
                >
                  Order
                </button>
              )}
            </div>
            );
          })}
        </div>
        )}
        {/* Login modal is rendered here so it can open via context */}
        <LoginModal />
      </div>

      {/* Modal */}
      {isModalOpen && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={closeModal}
            aria-hidden
          />

    <div className="relative bg-white rounded-lg sm:rounded-xl md:rounded-2xl max-w-[70vw] sm:max-w-md md:max-w-2xl lg:max-w-3xl w-full max-h-[65vh] sm:max-h-[85vh] md:max-h-[90vh] overflow-y-auto p-2 sm:p-4 md:p-6 lg:p-8 text-black shadow-2xl border border-gray-200">
                <button
                  onClick={closeModal}
                  className="absolute top-1 right-1 sm:top-2 sm:right-2 md:top-3 md:right-3 w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 flex items-center justify-center text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors z-10 text-[10px] sm:text-base"
                  aria-label="Close"
                >
                  ✕
                </button>

            <div className="flex flex-col md:flex-row items-stretch gap-2 sm:gap-3 md:gap-5 lg:gap-6">
              {/* hero-left layout: image on the left (larger) on md+, stacked on small screens */}
              <div className="md:w-1/3 w-full h-24 sm:h-40 md:h-auto rounded overflow-hidden bg-gray-100 flex-shrink-0">
                {selectedItem.img ? (
                  <img src={selectedItem.img} alt={selectedItem.title} className="w-full h-full object-cover object-center block" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-7 h-7 sm:w-12 sm:h-12 text-gray-300" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="32" cy="32" r="30" stroke="#e5e7eb" strokeWidth="2" />
                    </svg>
                  </div>
                )}
              </div>

              <div className="flex-1 flex flex-col justify-start min-w-0">
                <h3 className="text-[13px] sm:text-lg md:text-xl lg:text-2xl font-montserrat mb-1 sm:mb-2 pr-5 font-semibold leading-tight">{selectedItem.title}</h3>
                <p className="text-[9px] sm:text-xs md:text-sm text-gray-600 mb-1 sm:mb-2 md:mb-3 line-clamp-2">{selectedItem.description || 'Delicious dish from Casa Gatchalian. Add it to your tray and enjoy!'}</p>
                <p className="text-[11px] sm:text-base md:text-lg font-semibold mb-2 sm:mb-3 text-gray-900">{selectedItem.price}</p>

                <div className="flex items-center gap-1 sm:gap-3 mb-2 sm:mb-4">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <button
                      onClick={() => changeQty(-1)}
                      aria-label="Decrease quantity"
                      disabled={qty <= 1}
                      className={`w-6 h-6 sm:w-9 sm:h-9 md:w-9 md:h-9 flex items-center justify-center rounded-full border border-gray-300 text-gray-700 transition-colors text-xs sm:text-base ${qty <= 1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100 active:scale-95'}`}
                    >
                      −
                    </button>

                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={qty}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (Number.isNaN(v)) {
                          setQty(1);
                        } else {
                          setQty(Math.max(1, v));
                        }
                      }}
                      onBlur={() => setQty((q) => Math.max(1, q))}
                      aria-label="Quantity"
                      className="no-spinner w-9 sm:w-14 md:w-16 text-center font-medium text-[11px] sm:text-base text-gray-800 border border-gray-300 rounded px-1 sm:px-2 py-0.5 sm:py-1"
                    />

                    <button
                      onClick={() => changeQty(1)}
                      aria-label="Increase quantity"
                      className="w-6 h-6 sm:w-9 sm:h-9 md:w-9 md:h-9 flex items-center justify-center rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors active:scale-95 text-xs sm:text-base"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-3">
                  {(selectedItem.status === 'unavailable' || selectedItem.status === 'sold_out') ? (
                    <div className="bg-gray-200 text-gray-600 px-3 sm:px-6 md:px-8 py-1 sm:py-2.5 rounded-full text-[10px] sm:text-sm md:text-base w-full sm:w-auto font-medium text-center">
                      {selectedItem.status === 'sold_out' ? 'Sold Out' : 'Unavailable'}
                    </div>
                  ) : (
                    <button
                      onClick={addToTray}
                      className="bg-black hover:bg-gray-800 text-white px-3 sm:px-6 md:px-8 py-1 sm:py-2.5 rounded-full text-[10px] sm:text-sm md:text-base transition-colors active:scale-95 w-full sm:w-auto font-medium"
                    >
                      Add to My Tray
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default MenuSection;
